import type { AdIntelligenceRecordsActivityForIndustryResponse } from "@similarweb/pro-api-client";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { useApiQuery } from "app/modules/api-client";
import { INDUSTRY_ENDPOINT_MIN_DATE } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/industryDataAvailability";
import type { FlattenedEntitiesData } from "app/pages/digital-marketing/ad-intelligence/common/overview/hooks/useActivityBreakdownData";
import type { PerformanceResponse } from "app/pages/digital-marketing/ad-intelligence/common/overview/types";

import { useIndustryAvailabilityParams } from "../useIndustryAvailabilityParams";

const mapActivityToPerformanceData = (
    data?: AdIntelligenceRecordsActivityForIndustryResponse,
): PerformanceResponse | undefined => {
    if (!data) {
        return undefined;
    }
    return {
        spend: { value: data.spend.value ?? 0, change: data.spend.change ?? 0 },
        impressions: { value: data.impressions.value ?? 0, change: data.impressions.change ?? 0 },
        visits: { value: data.visits.value ?? 0, change: data.visits.change ?? 0 },
        hasAdIntelMetrics: false,
    };
};

const mapActivityToEntitiesData = (
    data?: AdIntelligenceRecordsActivityForIndustryResponse,
): FlattenedEntitiesData<"advertiser"> | undefined => {
    if (!data) {
        return undefined;
    }
    return {
        favicon: "",
        campaigns: { count: data.campaigns.value ?? 0, change: data.campaigns.change ?? 0 },
        creatives: { count: data.ads.value ?? 0, change: data.ads.change ?? 0 },
        publishers: { count: data.publishers.value ?? 0, change: data.publishers.change ?? 0 },
    };
};

export const useIndustryActivity = (category: IFlattenedCategory) => {
    const { params, availability } = useIndustryAvailabilityParams(
        category,
        INDUSTRY_ENDPOINT_MIN_DATE.adintelligenceIndustryActivityList,
    );
    const { data, isLoading, isFetching, isError } = useApiQuery(
        {
            tag: "AdIntelligenceMarketingAssets",
            methodName: "adintelligenceIndustryActivityList",
            options: { enabled: !availability.isFullyUnavailable },
        },
        params,
    );

    return {
        performanceData: mapActivityToPerformanceData(data),
        entitiesData: mapActivityToEntitiesData(data),
        isWindow: params.isWindow,
        isLoading: isLoading || isFetching,
        isError,
        availability,
    };
};
